import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { components } from "@/api/types";
import { actionKey, apiPost, qk, resetActionKey } from "@/shared/api";

type TopUpResult = components["schemas"]["TopUpResult"];

/**
 * Пополнение работающего робота (ТЗ §18).
 *
 * Ключ идемпотентности включает СУММУ. У покупки он тоже привязан к действию,
 * а не к попытке отправки, но здесь сумма — часть тела, и бэкенд считает
 * отпечаток вместе с ней: тот же ключ с другой суммой он отвергает 409
 * (см. `resetActionKey` в `shared/api/idempotency.ts`). Без суммы в ключе
 * пользователь, поправивший сумму после ошибки, получал бы конфликт вместо
 * новой попытки.
 *
 * `qk.profile()` в инвалидации нет намеренно: пополнение двигает капитал, но
 * не число роботов, а профиль показывает именно его.
 */
export function useTopupRobot(userRobotId: number) {
  const client = useQueryClient();

  return useMutation<TopUpResult, unknown, string>({
    mutationFn: (amount: string) =>
      apiPost("/api/v1/my-robots/{user_robot_id}/topup", {
        params: { user_robot_id: userRobotId },
        body: { amount },
        idempotencyKey: actionKey(`topup:${String(userRobotId)}:${amount}`),
      }),
    onSuccess: (_result, amount) => {
      resetActionKey(`topup:${String(userRobotId)}:${amount}`);
      // Префиксы, а не точные ключи: и главная, и деталь робота
      // параметризованы периодом графика.
      for (const key of [
        ["home"],
        // Пополнение расходует пул лимитированного робота наравне с запуском,
        // значит двигает и шкалу в каталоге, и остаток на карточке.
        ["robots"],
        qk.wallet(),
        qk.myRobots(),
        ["my-robots", "detail", userRobotId],
      ]) {
        void client.invalidateQueries({ queryKey: key });
      }
      void client.invalidateQueries({ queryKey: ["wallet", "transactions"] });
      void client.invalidateQueries({ queryKey: ["activity"] });
    },
  });
}
